import React, { useState } from 'react'
import { useSelector } from 'react-redux'
import {
  Box,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Paper,
  Typography,
  Button,
  useTheme,
  useMediaQuery,
  Link,
} from '@mui/material'
import MapIcon from '@mui/icons-material/Map'
import PhoneIcon from '@mui/icons-material/Phone'
import LocationOnIcon from '@mui/icons-material/LocationOn'
import EmailIcon from '@mui/icons-material/Email'
import DirectoryContactDialog from 'src/components/Dialog/DirectoryContactDialog'

const DirectoryCard = ({ system, signedIn, theme, isCampus }) => {
  const parentEmails = system.employerUserEmails?.filter(
    email => !system.campuses?.some(campus => (campus.employerUserEmails || []).includes(email)),
  ) || []
  const emails = isCampus ? (system.employerUserEmails || []) : parentEmails

  return (
    <Paper
      elevation={isCampus ? 0 : 1}
      sx={{
        p: 2,
        mb: isCampus ? 0 : 1.5,
        ml: isCampus ? 2 : 0,
        borderLeft: isCampus ? `3px solid ${theme.palette.grey[300]}` : 'none',
        backgroundColor: isCampus ? theme.palette.grey[50] : 'white',
      }}
    >
      <Typography variant={isCampus ? 'body2' : 'body1'} fontWeight='bold' sx={{ mb: 1 }}>
        {system.name}
      </Typography>
      {system.address && (
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 0.5 }}>
          <LocationOnIcon sx={{ fontSize: 16, color: theme.palette.grey[500], mt: 0.25 }} />
          <Typography variant='body2' color='text.secondary'>
            {system.address}
          </Typography>
        </Box>
      )}
      {system.phone && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
          <PhoneIcon sx={{ fontSize: 16, color: theme.palette.grey[500] }} />
          <Typography variant='body2'>
            <a href={`tel:${system.phone}`} style={{ color: theme.palette.primary.main, textDecoration: 'none' }}>
              {system.phone}
            </a>
          </Typography>
        </Box>
      )}
      {signedIn && emails.length > 0 && (
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mt: 0.5 }}>
          <EmailIcon sx={{ fontSize: 16, color: theme.palette.grey[500], mt: 0.25 }} />
          <Box>
            {emails.map((email, index) => (
              <Typography key={index} variant='body2' sx={{ wordBreak: 'break-all' }}>
                <a href={`mailto:${email}`} style={{ color: theme.palette.primary.main }}>{email}</a>
              </Typography>
            ))}
          </Box>
        </Box>
      )}
    </Paper>
  )
}

const DirectoryTable = ({
  healthcareSystems,
  sortConfig = { key: 'name', direction: 'asc' },
  sortData,
}) => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const [feedbackOpen, setFeedbackOpen] = useState(false)
  const { signedIn } = useSelector(state => state.identity)
  const openGlobalMap = () => {
    window.open(
      `https://www.google.com/maps/d/u/0/viewer?ll=43.16822275184898%2C-77.0396822523789&z=7&mid=1yP9yZeA4N0o3dQgp4KTVrgJjy9LjKEQ`,
      '_blank',
    )
  }

  return (
    <Grid
      container
      spacing={2}
      alignItems='center'
      justifyContent='center'
      sx={{
        marginBottom: 3,
        padding: { xs: '12px', md: '20px' },
        width: '100%',
        maxWidth: '100%',
      }}
    >
      {/* Action Buttons */}
      {signedIn && (
        <Grid
          container
          justifyContent='space-between'
          direction={{ xs: 'column', sm: 'row' }}
          spacing={{ xs: 1, sm: 0 }}
          sx={{ marginTop: 1, marginBottom: 1, width: '100%' }}
        >
          <Grid item>
            <Button
              variant='contained'
              startIcon={<MapIcon />}
              onClick={openGlobalMap}
              fullWidth={isMobile}
              sx={{
                fontWeight: 'bold',
                backgroundColor: theme.palette.primary.main,
                color: theme.palette.primary.contrastText,
                padding: { xs: '10px 16px', md: '12px 24px' },
                fontSize: { xs: '0.875rem', md: '1rem' },
                borderRadius: '8px',
                '&:hover': {
                  backgroundColor: theme.palette.primary.dark,
                },
              }}
            >
              Use our Interactive Map
            </Button>
          </Grid>
          <Grid item>
            <Button
              variant='outlined'
              color='primary'
              onClick={() => setFeedbackOpen(true)}
              fullWidth={isMobile}
            >
              Report Incorrect Information
            </Button>
          </Grid>
        </Grid>
      )}

      {/* Signed-out prompt */}
      {!signedIn && (
        <Paper sx={{ width: '100%', p: { xs: 2, md: 3 }, textAlign: 'center', mb: 2 }}>
          <Typography variant='body1' color='textSecondary'>
            Finding the right job in healthcare just got easier. MyStethi now features one
            of the <b>largest hospital directories</b>, a comprehensive list of in-house
            physician recruiters, and an <b>interactive map</b> to help you quickly explore
            opportunities across the country.
          </Typography>
          <Typography
            variant='body1'
            color='textSecondary'
            sx={{ textAlign: 'left', display: 'inline-block', marginTop: 1 }}
          >
            ✅ Browse thousands of hospitals and healthcare systems in one place <br />
            ✅ Connect directly with in-house recruiters for job opportunities <br />
            ✅ Use our interactive map to find hospitals and recruiters by location
          </Typography>

          <Typography variant='body1' color='textSecondary' sx={{ marginTop: 2 }}>
            To respect the privacy of our users, we ask that you log in or sign up below.
          </Typography>

          <Link
            href='/login'
            underline='hover'
            color='primary'
            sx={{ marginRight: '20px', marginTop: '10px', display: 'inline-block' }}
          >
            Sign in
          </Link>

          <Button
            variant='contained'
            color='primary'
            sx={{ mt: 1 }}
            component={Link}
            href='/register/physicians'
          >
            Sign Up
          </Button>
        </Paper>
      )}

      {/* Mobile: Card Layout */}
      {isMobile && (
        <Box sx={{ width: '100%' }}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mb: 2,
              px: 0.5,
            }}
          >
            <Button
              size='small'
              onClick={() => sortData('name')}
              sx={{ textTransform: 'none', fontWeight: 600, color: theme.palette.grey[700] }}
            >
              Sort by Name {sortConfig.key === 'name' ? (sortConfig.direction === 'asc' ? '↑' : '↓') : ''}
            </Button>
          </Box>
          {healthcareSystems.map(system => (
            <React.Fragment key={system.id}>
              <DirectoryCard system={system} signedIn={signedIn} theme={theme} isCampus={false} />
              {system.campuses?.map((campus, index) => (
                <DirectoryCard
                  key={`${system.id}-campus-${index}`}
                  system={campus}
                  signedIn={signedIn}
                  theme={theme}
                  isCampus
                />
              ))}
            </React.Fragment>
          ))}
        </Box>
      )}

      {/* Desktop: Table Layout */}
      {!isMobile && (
        <TableContainer
          sx={{
            width: '100%',
            overflowX: 'auto',
          }}
          component={Paper}
        >
          <Table>
            <TableHead sx={{ backgroundColor: theme.palette.secondary.dark }}>
              <TableRow>
                <TableCell sx={{ color: 'white' }}>
                  <TableSortLabel
                    active={sortConfig.key === 'name'}
                    direction={sortConfig.direction || 'asc'}
                    onClick={() => sortData('name')}
                    sx={{
                      color: 'white',
                      '&:hover': { color: 'white' },
                      '&.Mui-active': { color: 'white' },
                      '& .MuiTableSortLabel-icon': { color: 'white' },
                    }}
                  >
                    Healthcare System
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ color: 'white' }}>Address</TableCell>
                <TableCell sx={{ color: 'white' }}>Phone Number</TableCell>
                <TableCell sx={{ color: 'white' }}>Recruiter Emails</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {healthcareSystems.map(system => (
                <React.Fragment key={system.id}>
                  <TableRow>
                    <TableCell>
                      <Typography variant='body1' fontWeight='bold'>
                        {system.name}
                      </Typography>
                    </TableCell>
                    <TableCell>{system.address}</TableCell>
                    <TableCell>{system.phone}</TableCell>
                    <TableCell>
                      {signedIn &&
                        system.employerUserEmails &&
                        system.employerUserEmails
                          .filter(
                            email =>
                              !system.campuses?.some(campus =>
                                (campus.employerUserEmails || []).includes(email),
                              ),
                          )
                          .map((email, index) => (
                            <Typography
                              key={index}
                              variant='body2'
                              sx={{
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                              }}
                            >
                              <a href={`mailto:${email}`}>{email}</a>
                            </Typography>
                          ))}
                    </TableCell>
                  </TableRow>

                  {system.campuses &&
                    system.campuses.map((campus, index) => (
                      <TableRow
                        key={`${system.id}-campus-${index}`}
                        sx={{ backgroundColor: theme.palette.grey[50] }}
                      >
                        <TableCell>
                          <Typography variant='body2' sx={{ paddingLeft: 2 }}>
                            {campus.name}
                          </Typography>
                        </TableCell>
                        <TableCell>{campus.address}</TableCell>
                        <TableCell>{campus.phone}</TableCell>
                        <TableCell>
                          {signedIn &&
                            (campus.employerUserEmails || []).map((email, index) => (
                              <Typography
                                key={index}
                                variant='body2'
                                sx={{
                                  whiteSpace: 'nowrap',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                }}
                              >
                                <a href={`mailto:${email}`}>{email}</a>
                              </Typography>
                            ))}
                        </TableCell>
                      </TableRow>
                    ))}
                </React.Fragment>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <DirectoryContactDialog feedbackOpen={feedbackOpen} setFeedbackOpen={setFeedbackOpen} />
    </Grid>
  )
}

export default DirectoryTable
